import { MonoTypeOperatorFunction, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DestroyRefPolyfill } from './destroy-ref.polyfill';

/**
 * takeUntilDestroyed Operator
 *
 * Angular 15 equivalent for Angular 16+ takeUntilDestroyed operator.
 * Automatically unsubscribes from an observable when the component is destroyed.
 *
 * Usage:
 * ```typescript
 * export class MyComponent implements OnDestroy {
 *   private destroyRef = new DestroyRefPolyfill();
 *
 *   ngOnInit() {
 *     this.someObservable$
 *       .pipe(takeUntilDestroyed(this.destroyRef))
 *       .subscribe();
 *   }
 *
 *   ngOnDestroy() {
 *     this.destroyRef.destroy();
 *   }
 * }
 * ```
 *
 * @param destroyRef The DestroyRefPolyfill instance to use for cleanup
 * @returns An operator function that unsubscribes when the component is destroyed
 */
export function takeUntilDestroyed<T>(
  destroyRef: DestroyRefPolyfill
): MonoTypeOperatorFunction<T> {
  return (source: Observable<T>): Observable<T> => {
    return source.pipe(takeUntil(destroyRef.destroyed$));
  };
}

/**
 * Alternative implementation that accepts an Observable directly
 * for cases where you want to use a custom destroy$ Subject.
 *
 * Usage:
 * ```typescript
 * export class MyComponent implements OnDestroy {
 *   private destroy$ = new Subject<void>();
 *
 *   ngOnInit() {
 *     this.someObservable$
 *       .pipe(takeUntilDestroyed$(this.destroy$))
 *       .subscribe();
 *   }
 *
 *   ngOnDestroy() {
 *     this.destroy$.next();
 *     this.destroy$.complete();
 *   }
 * }
 * ```
 */
export function takeUntilDestroyed$<T>(
  destroyed$: Observable<void>
): MonoTypeOperatorFunction<T> {
  return (source: Observable<T>): Observable<T> => {
    return source.pipe(takeUntil(destroyed$));
  };
}
